/**
 * When a tutor is available, as the diary and the editor both understand it.
 *
 * Kept here rather than on either side because both need the same answers and
 * they must not drift: the editor decides what may be saved, the diary decides
 * what to draw, and a period the editor allows but the grid cannot place is a
 * bug that only shows up on screen.
 *
 * **Everything is minutes from midnight, local to the school.** The calendar
 * places lessons the same way, so a working period and a lesson are directly
 * comparable without either side converting anything.
 *
 * **Nothing recorded means unknown, not unavailable.** A tutor nobody has
 * filled in has no rows, and the diary draws them exactly as it did before any
 * of this existed. Shading a whole week shut because an owner has not got to
 * somebody yet would be a confident claim about a person's availability that
 * nobody made.
 */

/** Minutes in a day. A period may end here — midnight — but not pass it. */
export const MINUTES_IN_DAY = 1440;

/** Monday is 0, matching the calendar's Monday-first week. */
export const WEEKDAY_COUNT = 7;

/** One stretch of one day. */
export interface Period {
  startsMinutes: number;
  endsMinutes: number;
}

/** A period with the day it belongs to. What the database stores. */
export interface WorkingPeriod extends Period {
  weekday: number;
}

/** "09:00", for a control and for a screen reader. */
export function clockOf(minutes: number): string {
  const whole = Math.max(0, Math.min(MINUTES_IN_DAY, Math.round(minutes)));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${String(hours).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

/**
 * "09:00" back to minutes, or null.
 *
 * Total, because it reads whatever a time control hands over — which on a
 * browser that does not support `type="time"` is a free-text box.
 */
export function minutesOfClock(clock: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock.trim());
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (minutes > 59) return null;

  const total = hours * 60 + minutes;
  return total > MINUTES_IN_DAY ? null : total;
}

/** Whether a period is one the grid can draw and the database will accept. */
export function isUsablePeriod(period: Period): boolean {
  const { startsMinutes: from, endsMinutes: to } = period;
  if (!Number.isInteger(from) || !Number.isInteger(to)) return false;
  if (from < 0 || to > MINUTES_IN_DAY) return false;
  return to > from;
}

export function isWeekday(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value < WEEKDAY_COUNT;
}

/**
 * Put a day's periods in order and join the ones that touch.
 *
 * Two reasons, and the second is the real one. Overlapping bands drawn on top
 * of each other are darker where they overlap, so "9–12 and 11–2" would show a
 * stripe at eleven that means nothing. And a day held as 9–12, 12–4 is the same
 * availability as 9–4, so storing it as two rows makes the editor show a
 * division the tutor did not make and cannot remove.
 *
 * Touching counts as overlapping: a period ending at 12:00 and one starting at
 * 12:00 are one stretch with a line drawn through it.
 */
export function tidyPeriods(periods: readonly Period[]): Period[] {
  const usable = periods
    .filter(isUsablePeriod)
    .sort((a, b) => a.startsMinutes - b.startsMinutes || a.endsMinutes - b.endsMinutes);

  const joined: Period[] = [];
  for (const period of usable) {
    const last = joined[joined.length - 1];
    if (last && period.startsMinutes <= last.endsMinutes) {
      last.endsMinutes = Math.max(last.endsMinutes, period.endsMinutes);
      continue;
    }
    joined.push({ ...period });
  }

  return joined;
}

/** A whole week, tidied, as seven lists indexed by weekday. */
export function byWeekday(periods: readonly WorkingPeriod[]): Period[][] {
  const week: Period[][] = Array.from({ length: WEEKDAY_COUNT }, () => []);
  for (const period of periods) {
    if (!isWeekday(period.weekday)) continue;
    week[period.weekday]?.push({
      startsMinutes: period.startsMinutes,
      endsMinutes: period.endsMinutes,
    });
  }
  return week.map(tidyPeriods);
}

/** Flatten a week back into rows, dropping anything unusable. */
export function flattenWeek(week: readonly (readonly Period[])[]): WorkingPeriod[] {
  const rows: WorkingPeriod[] = [];
  week.slice(0, WEEKDAY_COUNT).forEach((periods, weekday) => {
    for (const period of tidyPeriods(periods)) rows.push({ weekday, ...period });
  });
  return rows;
}

/**
 * The stretches of `from`–`to` that no period covers.
 *
 * The calendar draws these rather than the working hours themselves. Shading
 * what is *closed* leaves the open time looking exactly as an unshaded column
 * does, which matters because the column already carries hour lines, a "today"
 * tint and the lesson cards — repainting the open hours would have to compose
 * with all three, while laying a wash over the closed ones composes with
 * nothing.
 *
 * An empty list of periods gives the whole span back: with a rota that says
 * nothing is worked, nothing is open. Callers distinguish *no rota* from *an
 * empty rota* before they get here.
 */
export function gapsAround(
  periods: readonly Period[],
  from: number,
  to: number,
): Period[] {
  if (to <= from) return [];

  const gaps: Period[] = [];
  let edge = from;

  for (const period of tidyPeriods(periods)) {
    // Clipped to the span, so a rota reaching outside the hours the grid draws
    // does not produce a band with a negative start.
    const start = Math.max(period.startsMinutes, from);
    const end = Math.min(period.endsMinutes, to);
    if (end <= edge) continue;
    if (start > edge) gaps.push({ startsMinutes: edge, endsMinutes: start });
    edge = end;
  }

  if (edge < to) gaps.push({ startsMinutes: edge, endsMinutes: to });
  return gaps;
}

/** How long a day's periods add up to, for "6h 30m in on Tuesday". */
export function minutesInDay(periods: readonly Period[]): number {
  return tidyPeriods(periods).reduce(
    (total, period) => total + (period.endsMinutes - period.startsMinutes),
    0,
  );
}

/** "6h 30m", or "6h", or "30m". Empty string for nothing at all. */
export function describeMinutes(minutes: number): string {
  if (minutes <= 0) return "";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
}

/**
 * Whether a moment falls inside any of the day's periods.
 *
 * Half open — a period ending at 17:00 does not contain 17:00 — so a lesson
 * finishing exactly on the hour is not counted as running past the end of the
 * day.
 */
export function isWithin(periods: readonly Period[], minutes: number): boolean {
  return periods.some(
    (period) => minutes >= period.startsMinutes && minutes < period.endsMinutes,
  );
}

/**
 * A lesson booked where nobody said the tutor was working.
 *
 * Worth its own function because it is the interesting disagreement: the rota
 * says one thing and the diary says another, and which is wrong is a question
 * for a person. Only asked of tutors who *have* a rota — with no rows there is
 * nothing to be outside of.
 */
export function fallsOutside(periods: readonly Period[], lesson: Period): boolean {
  if (periods.length === 0) return false;
  return !tidyPeriods(periods).some(
    (period) =>
      lesson.startsMinutes >= period.startsMinutes && lesson.endsMinutes <= period.endsMinutes,
  );
}
